/**
 * Locale-aware date formatting.
 *
 * EN uses `en-US`, DE uses `de-CH` (see {@link ./config.ts}). Centralised here
 * so every surface formats dates consistently per locale instead of hand-rolled
 * `toLocaleDateString` calls scattered across pages.
 */
import { LOCALE_INTL } from './config'
import type { Locale } from './config'

const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
}

/** A bare calendar date with no time component, e.g. `2026-06-12`. */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/u

export function formatDate(
  value: string | number | Date,
  locale: Locale,
  options: Intl.DateTimeFormatOptions = DEFAULT_DATE_OPTIONS,
): string {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  // A date-only string ('YYYY-MM-DD') parses as UTC midnight. Formatting it in
  // the viewer's local timezone would render the previous calendar day for
  // anyone west of UTC (negative offset). Pin date-only inputs to UTC so the
  // intended calendar date is preserved everywhere. Full ISO timestamps and
  // numeric/Date inputs keep their wall-clock-local behavior unless the caller
  // overrides the timeZone explicitly.
  const isDateOnly = typeof value === 'string' && DATE_ONLY_RE.test(value)
  const resolvedOptions =
    isDateOnly && options.timeZone === undefined
      ? { ...options, timeZone: 'UTC' }
      : options
  return new Intl.DateTimeFormat(LOCALE_INTL[locale], resolvedOptions).format(
    date,
  )
}
